import React, { useState } from 'react';
import styled from 'styled-components';

// Styled Components
const SectionWrapper = styled.section`
  padding: 100px 0;
  background-color: var(--bg-white);
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: var(--text-dark);
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionDescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-light);
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background-color: var(--bg-white);
  border-radius: 12px;
  box-shadow: var(--shadow-lg);
  overflow: hidden;
`;

const FormContent = styled.div`
  padding: 2.5rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-dark);
  font-size: 0.9rem;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--bg-light);
  background-color: white;
  transition: border-color var(--transition-fast);
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--bg-light);
  background-color: white;
  transition: border-color var(--transition-fast);
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--bg-light);
  background-color: white;
  transition: border-color var(--transition-fast);
  resize: vertical;
  min-height: 120px;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem;
  background-color: var(--primary);
  color: white;
  font-weight: 600;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: background-color var(--transition-fast);
  
  &:hover {
    background-color: var(--primary-dark);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.3);
  }
  
  &:disabled {
    background-color: var(--text-light);
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  background-color: #ECFDF5;
  color: #065F46;
  padding: 1rem;
  border-radius: var(--radius-md);
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.75rem;
    flex-shrink: 0;
  }
`;

// CheckCircle Icon for success message
const CheckCircleIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86" stroke="#065F46" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M22 4L12 14.01L9 11.01" stroke="#065F46" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// Contact Form Component
const ContactForm = () => {
    const [formData, setFormData] = useState({
        name: '',
        company: '',
        email: '',
        phone: '',
        interest: '',
        message: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // 模擬表單提交
        setTimeout(() => {
            setIsSubmitting(false);
            setIsSuccess(true);
            setFormData({
                name: '',
                company: '',
                email: '',
                phone: '',
                interest: '',
                message: ''
            });

            // 重置成功訊息
            setTimeout(() => {
                setIsSuccess(false);
            }, 5000);
        }, 1500);
    };

    return (
        <SectionWrapper id="contact">
            <SectionContainer>
                <SectionHeader>
                    <SectionTitle>聯絡我們</SectionTitle>
                    <SectionDescription>
                        想了解更多關於 ShopWave 如何幫助您的業務成長？填寫以下表單，我們將盡快與您聯繫。
                    </SectionDescription>
                </SectionHeader>

                <FormContainer>
                    <FormContent>
                        {isSuccess && (
                            <SuccessMessage>
                                <CheckCircleIcon />
                                <span>您的訊息已成功送出！我們將盡快與您聯繫。</span>
                            </SuccessMessage>
                        )}

                        <form onSubmit={handleSubmit}>
                            <FormRow>
                                <FormGroup>
                                    <FormLabel htmlFor="name">姓名</FormLabel>
                                    <FormInput
                                        type="text"
                                        id="name"
                                        name="name"
                                        placeholder="請輸入您的姓名"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <FormLabel htmlFor="company">公司名稱</FormLabel>
                                    <FormInput
                                        type="text"
                                        id="company"
                                        name="company"
                                        placeholder="請輸入公司名稱"
                                        value={formData.company}
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </FormRow>

                            <FormRow>
                                <FormGroup>
                                    <FormLabel htmlFor="email">電子郵件</FormLabel>
                                    <FormInput
                                        type="email"
                                        id="email"
                                        name="email"
                                        placeholder="請輸入您的電子郵件"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <FormLabel htmlFor="phone">電話</FormLabel>
                                    <FormInput
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        placeholder="請輸入您的電話號碼"
                                        value={formData.phone}
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </FormRow>

                            <FormGroup>
                                <FormLabel htmlFor="interest">您對哪種方案有興趣？</FormLabel>
                                <FormSelect
                                    id="interest"
                                    name="interest"
                                    value={formData.interest}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="" disabled>請選擇</option>
                                    <option value="basic">入門方案</option>
                                    <option value="standard">標準方案</option>
                                    <option value="enterprise">企業方案</option>
                                    <option value="not-sure">尚未確定</option>
                                </FormSelect>
                            </FormGroup>

                            <FormGroup>
                                <FormLabel htmlFor="message">訊息</FormLabel>
                                <FormTextarea
                                    id="message"
                                    name="message"
                                    placeholder="請輸入您的需求或疑問"
                                    value={formData.message}
                                    onChange={handleChange}
                                    required
                                />
                            </FormGroup>

                            <SubmitButton type="submit" disabled={isSubmitting}>
                                {isSubmitting ? '處理中...' : '提交'}
                            </SubmitButton>
                        </form>
                    </FormContent>
                </FormContainer>
            </SectionContainer>
        </SectionWrapper>
    );
};

export default ContactForm;